import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, Optional } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { TranslateModule } from '@ngx-translate/core'
import * as PresenterContainerActions from 'libs/gb-dashboard/presenter/data-access-projector-containers/src/lib/+state/projector-containers.actions'
import { ProjectorContainersFacade } from 'libs/gb-dashboard/presenter/data-access-projector-containers/src/lib/+state/projector-containers.facade'
import * as ProjectorActions from 'libs/gb-dashboard/projector/data-access/src/lib/+state/projectors.actions'
import { ProjectorsFacade } from 'libs/gb-dashboard/projector/data-access/src/lib/+state/projectors.facade'
import * as ProjectorTemplatesActions from 'libs/gb-dashboard/presenter/data-access-projector-templates/src/lib/+state/projector-templates.actions'
import { ProjectorTemplatesFacade } from 'libs/gb-dashboard/presenter/data-access-projector-templates/src/lib/+state/projector-templates.facade'
import { GbPresenterTemplatesListComponent } from 'libs/gb-dashboard/presenter/feature/src/lib/gb-presenter-templates-list/gb-presenter-templates-list.component'
import { combineLatest, map, take } from 'rxjs'

type SlideDialogMode = 'create' | 'duplicate'

interface AddSlideDialogData {
  projectorUuid?: string
  mode?: 'duplicate'
}

@UntilDestroy()
@Component({
  selector: 'gb-add-slide-dialog',
  templateUrl: './gb-add-slide-dialog.component.html',
  styleUrls: ['./gb-add-slide-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    TranslateModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatInputModule,
    MatCheckboxModule,
    GbPresenterTemplatesListComponent,
  ],
})
export class GbAddSlideDialogComponent implements OnDestroy {
  editCreateLoaded$ = this.projectorsFacade.editCreateLoaded$
  selectedContainer$ = this.projectorContainersFacade.selectedProjectorContainer$
  selectedTemplate$ = this.projectorTemplatesFacade.selectedProjectorTemplate$
  dialogMode: SlideDialogMode = this.dialogData?.mode === 'duplicate' ? 'duplicate' : 'create'
  dialogTitleKey = this.dialogMode === 'duplicate' ? 'PROJECTOR.DUPLICATE_SLIDE' : 'PROJECTOR.ADD_SLIDE'

  addSlideFormFields = {
    slide_name: new FormControl('', [Validators.required, Validators.maxLength(30)]),
    use_template: new FormControl(false),
  }
  addSlideForm = new FormGroup(this.addSlideFormFields)

  get slide_name() {
    return this.addSlideForm.get('slide_name')
  }

  get use_template() {
    return this.addSlideForm.get('use_template')
  }

  private selectedTemplateUuid: string | undefined

  constructor(
    private projectorsFacade: ProjectorsFacade,
    private projectorContainersFacade: ProjectorContainersFacade,
    private projectorTemplatesFacade: ProjectorTemplatesFacade,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: AddSlideDialogData | null,
  ) {
    this.selectedTemplate$
      .pipe(
        map((template) => template?.uuid),
        untilDestroyed(this),
      )
      .subscribe((uuid) => {
        this.selectedTemplateUuid = uuid
      })

    combineLatest([this.projectorsFacade.allProjectors$, this.selectedContainer$])
      .pipe(
        take(1),
        map(([projectors, container]) => {
          if (!container?.uuid) {
            return
          }

          if (this.dialogMode === 'duplicate' && this.dialogData?.projectorUuid) {
            const sourceProjector = projectors.find((projector) => projector.uuid === this.dialogData?.projectorUuid)
            if (sourceProjector?.name) {
              this.slide_name?.setValue(`${sourceProjector.name} - copy`)
              return
            }
          }

          const slideCount = projectors.filter((projector) => projector.container === container.uuid).length
          this.slide_name?.setValue(`Slide ${slideCount + 1}`)
        }),
      )
      .subscribe()
  }

  ngOnDestroy(): void {
    this.projectorTemplatesFacade.dispatch(ProjectorTemplatesActions.clearSelectedProjectorTemplate())
  }

  submit() {
    if (this.use_template?.value && !this.selectedTemplateUuid) {
      this.addSlideForm.markAllAsTouched()
      return
    }
    this.addSlideForm.valid ? this.save() : this.onError()
  }

  onError() {
    this.addSlideForm.markAllAsTouched()
  }

  save() {
    this.selectedContainer$
      .pipe(
        take(1),
        map((container) => {
          if (!container?.uuid) {
            return
          }

          if (this.dialogMode === 'duplicate' && this.dialogData?.projectorUuid) {
            this.projectorContainersFacade.dispatch(
              PresenterContainerActions.duplicateSlide({
                projectorUuid: this.dialogData.projectorUuid,
                name: this.slide_name?.value || '',
              }),
            )
            return
          }

          this.projectorsFacade.dispatch(
            ProjectorActions.addSlide({
              name: this.slide_name?.value || '',
              containerUuid: container.uuid,
              templateUuid: this.use_template?.value ? this.selectedTemplateUuid : undefined,
            }),
          )
        }),
      )
      .subscribe()
  }
}
